import { useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { BusinessKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import dayjs from 'dayjs';
import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';
import { useGetAirSettingsQuery, useUpdateBusinessAirSettingsMutation } from '@frontend/api-ai-settings';
import { Trans, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { FloatingSaveBar } from '@frontend/settings-ui';
import { sentry } from '@frontend/tracking';
import { useUnsavedChanges } from '@frontend/unsaved-changes-modal';
import { useAlert, useModalControl } from '@frontend/design-system';
import {
  AIRSettingsDefaultSourcesProvider,
  useAIRSettingsDefaultSources,
} from '../../../../contexts/air-settings-default-sources.context';
import {
  AIRBusinessKnowledgeProvider,
  selectStoreIsDirty,
  useAIRBusinessKnowledgeStore,
} from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { LastUpdatedInfo } from '../../common/last-updated-info';
import { SettingsUnsavedChangesModal } from '../../common/settings-unsaved-changes-modal';
import { MultiLocationBusinessKnowledge } from './multi-location-business-knowledge';
import { SingleLocationBusinessKnowledge } from './single-location-business-knowledge';
import { initializeKnowledgeData, sanitizeKnowledgeDataForSave } from './utils';

const DEFAULT_BUSINESS_KNOWLEDGE: BusinessKnowledge = {};

const createInitialSettings = (locationIds: string[]): Record<string, BusinessKnowledge> => {
  return locationIds.reduce((acc, id) => {
    acc[id] = cloneDeep(DEFAULT_BUSINESS_KNOWLEDGE);
    return acc;
  }, {} as Record<string, BusinessKnowledge>);
};

const BusinessKnowledgePageContent = () => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { selectedLocationIds } = useAppScopeStore();
  const alerts = useAlert();
  const { isLoadingDefaultSources } = useAIRSettingsDefaultSources();

  const { initialSettings, draftSettings, setInitialSettings, setDraftSettings, saveDraft, clearDraft, resetStore } =
    useAIRBusinessKnowledgeStore([
      'initialSettings',
      'draftSettings',
      'setInitialSettings',
      'setDraftSettings',
      'saveDraft',
      'clearDraft',
      'resetStore',
    ]);

  const shouldProceedAfterSaveRef = useRef(false);

  const { data: airSettingsResponse, isLoading: isBusinessSettingsLoading } = useGetAirSettingsQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: selectedLocationIds.length > 0,
    },
  });

  const { mutate: updateBusinessAirSettings, isLoading: isUpdatingBusinessAirSettings } =
    useUpdateBusinessAirSettingsMutation({
      options: {
        onSuccess: () => {
          alerts.success(t('Business knowledge settings saved successfully.'));
          saveDraft();
          if (shouldProceedAfterSaveRef.current) {
            shouldProceedAfterSaveRef.current = false;
            proceedWithUnsavedChanges();
          }
        },
        onError: (error) => {
          shouldProceedAfterSaveRef.current = false;
          alerts.error(t('Failed to save business knowledge settings. Please try again.'));
          sentry.error({
            team: 'frontend',
            topic: 'ai-receptionist',
            error,
          });
        },
      },
    });

  useEffect(() => {
    const businessSettings = airSettingsResponse?.locationAirSettings?.reduce((acc, curr) => {
      if (curr.locationId && curr.airSettings?.businessKnowledge) {
        acc[curr.locationId] = initializeKnowledgeData(curr.airSettings.businessKnowledge);
      }
      return acc;
    }, {} as Record<string, BusinessKnowledge>);

    if (businessSettings && Object.keys(businessSettings).length > 0) {
      setInitialSettings(cloneDeep(businessSettings));
      setDraftSettings(cloneDeep(businessSettings));
    } else {
      const defaultSettings = createInitialSettings(selectedLocationIds);
      setInitialSettings(cloneDeep(defaultSettings));
      setDraftSettings(cloneDeep(defaultSettings));
    }
  }, [airSettingsResponse, selectedLocationIds, setInitialSettings, setDraftSettings]);

  const isMultiLocation = selectedLocationIds.length > 1;

  const locationErrors = useAIRBusinessKnowledgeStore((state) => {
    return selectedLocationIds.reduce((acc, id) => {
      const errors = state.fieldErrors[id];
      acc[id] = errors ? Object.values(errors).some(Boolean) : false;
      return acc;
    }, {} as Record<string, boolean>);
  });

  const formHasErrors = useAIRBusinessKnowledgeStore((state) =>
    selectedLocationIds.some((id) => {
      const errors = state.fieldErrors[id];
      return errors ? Object.values(errors).some(Boolean) : false;
    })
  ) as boolean;
  const isDirty = useAIRBusinessKnowledgeStore(selectStoreIsDirty);

  const { modalProps: unsavedChangesModalProps, openModal: openUnsavedChangesModal } = useModalControl();
  const { proceed: proceedWithUnsavedChanges, cancel: cancelUnsavedChanges } = useUnsavedChanges(
    isDirty,
    unsavedChangesModalProps.show,
    openUnsavedChangesModal
  );

  const handleSave = useCallback(() => {
    if (formHasErrors || isUpdatingBusinessAirSettings) return;

    const updatedLocationDataList = Object.entries(draftSettings)
      .filter(([locationId, settings]) => {
        const initialSettingsForLocation = initialSettings[locationId] || {};
        return !isEqual(settings, initialSettingsForLocation);
      })
      .map(([locationId, settings]) => {
        const rawInitialData = airSettingsResponse?.locationAirSettings?.find((s) => s.locationId === locationId)
          ?.airSettings?.businessKnowledge;

        return {
          ...sanitizeKnowledgeDataForSave(settings, rawInitialData),
          locationId,
        };
      });

    updateBusinessAirSettings({
      businessKnowledge: updatedLocationDataList,
    });
  }, [
    formHasErrors,
    isUpdatingBusinessAirSettings,
    draftSettings,
    updateBusinessAirSettings,
    initialSettings,
    airSettingsResponse,
  ]);

  const handleProceedWithoutSaving = () => {
    clearDraft();
    unsavedChangesModalProps.onClose();
    proceedWithUnsavedChanges();
  };

  const lastUpdatedAt = useMemo(() => {
    const lastUpdatedAtList = airSettingsResponse?.locationAirSettings
      ?.map((settings) =>
        settings?.airSettings?.businessKnowledge?.lastUpdatedAt
          ? dayjs(settings.airSettings.businessKnowledge.lastUpdatedAt)
          : undefined
      )
      .filter((ts): ts is dayjs.Dayjs => !!ts)
      .sort((a, b) => b.valueOf() - a.valueOf());

    return lastUpdatedAtList?.[0]?.toISOString();
  }, [airSettingsResponse?.locationAirSettings]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, [resetStore]);

  const isLoading = isBusinessSettingsLoading || isUpdatingBusinessAirSettings || isLoadingDefaultSources;

  return (
    <div css={styles.container}>
      <div css={{ flex: 1, paddingBottom: '80px' }}>
        {isMultiLocation ? (
          <MultiLocationBusinessKnowledge
            locationIds={selectedLocationIds}
            settings={draftSettings}
            initialSettings={initialSettings}
            locationErrors={locationErrors}
            isLoading={isLoading}
          />
        ) : (
          <SingleLocationBusinessKnowledge locationId={selectedLocationIds[0]} isLoading={isLoading} />
        )}
      </div>

      <FloatingSaveBar
        isDirty={isDirty}
        hasErrors={formHasErrors}
        extraContent={lastUpdatedAt ? <LastUpdatedInfo lastUpdatedAt={lastUpdatedAt} /> : null}
        primaryAction={{
          label: t('Save'),
          onClick: handleSave,
          disabled: formHasErrors,
          trackingId: AiReceptionistTrackingIds.businessKnowledgeSaveButton,
          hoverLabel: t('Click to update AI knowledge base. Changes should apply soon after being updated.'),
          isLoading,
        }}
      />

      <SettingsUnsavedChangesModal
        show={unsavedChangesModalProps.show}
        onClose={() => {
          unsavedChangesModalProps.onClose();
          cancelUnsavedChanges();
        }}
        onProceedWithoutSaving={handleProceedWithoutSaving}
        onSave={() => {
          shouldProceedAfterSaveRef.current = true;
          handleSave();
          unsavedChangesModalProps.onClose();
        }}
        isSaveDisabled={formHasErrors}
        description={
          <Trans t={t}>
            Before you leave, <strong>would you like to save and push changes</strong> to your AI Receptionist's
            business knowledge?
          </Trans>
        }
      />
    </div>
  );
};

export const BusinessKnowledgePage = () => {
  const { selectedLocationIds } = useAppScopeStore();

  return (
    <AIRSettingsDefaultSourcesProvider locationIds={selectedLocationIds}>
      <AIRBusinessKnowledgeProvider>
        <BusinessKnowledgePageContent />
      </AIRBusinessKnowledgeProvider>
    </AIRSettingsDefaultSourcesProvider>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    position: relative;
    height: 100%;
  `,
};
